import React, { useState, useEffect } from "react";
import { Dialog, Box, Stack, Divider, ButtonProps, CircularProgress } from "@mui/material";
import ByzMuiButton from "../buttons/ByzMuiButton";
import ByzMuiTextField from "../inputs/ByzMuiTextField";
import ByzMuiTypography from "../inputs/ByzMuiTypography";
import { ByzMuiIconButton } from "../buttons/ByzMuiIconButton";

//CKAPISIZ;452953;30.04.2025
export interface CancelDialogProps {
  open: boolean;
  title: string;
  description?: string;
  placeholder?: string;
  extraAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    sx?: ButtonProps["sx"];
  };
  cancelText?: string;
  confirmText?: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  requireInput?: boolean;
  width?: string;
  height?: string;
  loading?: boolean;
  zIndex?: number;
}

const CancelDialog: React.FC<CancelDialogProps> = ({
  open,
  title,
  description,
  placeholder = "",
  extraAction,
  cancelText,
  confirmText,
  onClose,
  onConfirm,
  requireInput = true,
  width,
  height,
  loading = false,
  zIndex,
}) => {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const confirmDisabled = requireInput && !reason.trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEnforceFocus
      sx={{
        zIndex: zIndex ?? ((theme) => theme.zIndex.modal + 200),
      }}
      PaperProps={{
        sx: {
          width,
          height,
          borderRadius: "16px",
          padding: "20px",
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center">
          <img src="/assets/icons/alert/alert.svg" alt="Alert Icon" />
          <ByzMuiTypography isTitle sx={{ fontSize: "20px" }}>
            {title}
          </ByzMuiTypography>
        </Stack>
        <ByzMuiIconButton size="small" onClick={onClose} disabled={loading}>
          <img
            src="/assets/icons/toast/close.svg"
            alt="close"
            style={{ width: 32, height: 32 }}
          />
        </ByzMuiIconButton>
      </Stack>
      <Divider sx={{ borderColor: "#E1E1E1", borderBottomWidth: 2, my: 2 }} />
      {extraAction && (
        <Box sx={{ marginBottom: "16px" }}>
          <ByzMuiButton
            onClick={extraAction.onClick}
            icon={extraAction.icon}
            disabled={loading}
            sx={{
              ...extraAction.sx,
              borderRadius: 2,
              padding: "4px 12px",
            }}
          >
            {extraAction.label}
          </ByzMuiButton>
        </Box>
      )}
      {description && (
        <ByzMuiTypography isTitle sx={{ marginBottom: "16px" }}>
          {description}
        </ByzMuiTypography>
      )}
      <Box sx={{ marginBottom: "16px" }}>
        <ByzMuiTextField
          fullWidth
          multiline
          minRows={4}
          placeholder={placeholder}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={loading}
        />
      </Box>
      <Divider sx={{ borderColor: "black", borderBottomWidth: 2, my: 2 }} />
      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ marginTop: "16px" }}
      >
        <ByzMuiButton
          differentColor="blue"
          marginLeft={"0px"}
          sx={{
            borderRadius: "4px",
            width: "75px",
            height: "30px",
          }}
          onClick={onClose}
          disabled={loading}
        >
          {cancelText}
        </ByzMuiButton>
        <ByzMuiButton
          differentColor="light_blue"
          marginLeft={"0px"}
          sx={{
            borderRadius: "4px",
            width: "75px",
            height: "30px",
          }}
          onClick={() => onConfirm(reason)}
          disabled={confirmDisabled || loading}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {loading && <CircularProgress size={14} sx={{ color: "inherit" }} />}
            <span>{confirmText}</span>
          </Stack>
        </ByzMuiButton>
      </Stack>
    </Dialog>
  );
};

export default CancelDialog;
